import { Auth } from 'aws-amplify';
import { APIError, APIResponse } from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://apis.bnbcart.com';

interface RetryConfig {
  maxRetries: number;
  baseDelay: number;
  maxDelay: number;
}

class APIClient {
  private baseURL: string;
  private retryConfig: RetryConfig;
  private tokenCache: { token: string; expiry: number } | null = null;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
    this.retryConfig = {
      maxRetries: 3,
      baseDelay: 1000,
      maxDelay: 10000,
    };
  }

  private async getAuthHeaders(): Promise<Record<string, string>> {
    const baseHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    try {
      // Check if we have a cached token that's still valid
      if (this.tokenCache && Date.now() < this.tokenCache.expiry) {
        return {
          ...baseHeaders,
          'Authorization': `Bearer ${this.tokenCache.token}`,
        };
      }

      // Get fresh session and cache the token
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      const payload = JSON.parse(atob(token.split('.')[1]));
      
      // Cache token with 5 minute buffer before expiry
      this.tokenCache = {
        token,
        expiry: (payload.exp * 1000) - (5 * 60 * 1000),
      };
      
      return {
        ...baseHeaders,
        'Authorization': `Bearer ${token}`,
      };
    } catch (error) {
      // Clear invalid token cache
      this.tokenCache = null;
      return baseHeaders;
    }
  }

  private async refreshToken(): Promise<void> {
    try {
      // Clear cached token to force refresh
      this.tokenCache = null;
      
      // Refresh the session
      const currentUser = await Auth.currentAuthenticatedUser();
      await Auth.currentSession();
      
      console.log('Token refreshed successfully');
    } catch (error) {
      console.error('Token refresh failed:', error);
      // Clear token cache on refresh failure
      this.tokenCache = null;
      throw error;
    }
  }

  private async handleResponse<T>(response: Response): Promise<APIResponse<T>> {
    const contentType = response.headers.get('content-type');
    
    if (!response.ok) {
      let errorData: APIError;
      
      if (contentType && contentType.includes('application/json')) {
        try {
          errorData = await response.json();
        } catch {
          // Fallback if JSON parsing fails
          errorData = {
            error: {
              code: 'PARSE_ERROR',
              message: `Failed to parse error response: HTTP ${response.status}`,
            },
            timestamp: new Date().toISOString(),
            requestId: response.headers.get('x-request-id') || 'unknown',
          };
        }
      } else {
        errorData = {
          error: {
            code: this.getErrorCodeFromStatus(response.status),
            message: `HTTP ${response.status}: ${response.statusText}`,
          },
          timestamp: new Date().toISOString(),
          requestId: response.headers.get('x-request-id') || 'unknown',
        };
      }
      
      const error = new Error(errorData.error.message) as Error & { 
        status: number; 
        code: string; 
        requestId: string;
      };
      error.status = response.status;
      error.code = errorData.error.code;
      error.requestId = errorData.requestId;
      
      throw error;
    }

    if (contentType && contentType.includes('application/json')) {
      const jsonData = await response.json();
      
      // If the response already has the expected APIResponse structure, return it
      if (jsonData && typeof jsonData === 'object' && 'data' in jsonData) {
        return jsonData;
      }
      
      // Otherwise, wrap the response in the expected structure
      return {
        data: jsonData as T,
        success: true,
      };
    }

    // For non-JSON responses, return a generic success response
    return {
      data: null as T,
      success: true,
    };
  }

  private getErrorCodeFromStatus(status: number): string {
    switch (status) {
      case 400: return 'BAD_REQUEST';
      case 401: return 'UNAUTHORIZED';
      case 403: return 'FORBIDDEN';
      case 404: return 'NOT_FOUND';
      case 409: return 'CONFLICT';
      case 429: return 'RATE_LIMITED';
      case 500: return 'INTERNAL_SERVER_ERROR';
      case 502: return 'BAD_GATEWAY';
      case 503: return 'SERVICE_UNAVAILABLE';
      case 504: return 'GATEWAY_TIMEOUT';
      default: return 'UNKNOWN_ERROR';
    }
  }

  private async sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private shouldRetry(error: any, attempt: number): boolean {
    if (attempt >= this.retryConfig.maxRetries) {
      return false;
    }

    // Retry on network errors or 5xx server errors
    if (!error.status) {
      return true; // Network error
    }

    // Retry on specific HTTP status codes
    const retryableStatuses = [429, 500, 502, 503, 504];
    return retryableStatuses.includes(error.status);
  }

  private calculateDelay(attempt: number): number {
    const delay = this.retryConfig.baseDelay * Math.pow(2, attempt);
    const jitter = Math.random() * 0.1 * delay;
    return Math.min(delay + jitter, this.retryConfig.maxDelay);
  }

  private async makeRequest<T>(
    method: string,
    endpoint: string,
    data?: any,
    attempt: number = 0
  ): Promise<APIResponse<T>> {
    try {
      console.log(`API Client: Making ${method} request to ${endpoint} (attempt ${attempt + 1})`);
      
      const headers = await this.getAuthHeaders();
      
      // Log if authorization header is present (without exposing the token)
      const hasAuth = headers.Authorization ? 'present' : 'missing';
      console.log(`API Client: Authorization header ${hasAuth}`);
      
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method,
        headers,
        body: data ? JSON.stringify(data) : undefined,
      });

      console.log(`API Client: Response status ${response.status} for ${endpoint}`);

      // Handle 401 errors by attempting token refresh
      if (response.status === 401 && attempt === 0) {
        console.log('API Client: Received 401, attempting token refresh...');
        try {
          await this.refreshToken();
          console.log('API Client: Token refresh successful, retrying request...');
          return this.makeRequest<T>(method, endpoint, data, attempt + 1);
        } catch (refreshError) {
          // If refresh fails, let the original 401 error bubble up
          console.error('API Client: Token refresh failed:', refreshError);
        }
      }

      return this.handleResponse<T>(response);
    } catch (error: any) {
      console.error(`API Client: Request failed for ${endpoint}:`, error);
      
      if (this.shouldRetry(error, attempt)) {
        const delay = this.calculateDelay(attempt);
        console.log(`API Client: Retrying in ${delay}ms (attempt ${attempt + 1}/${this.retryConfig.maxRetries})`);
        
        await this.sleep(delay);
        return this.makeRequest<T>(method, endpoint, data, attempt + 1);
      }
      
      throw error;
    }
  }

  async get<T>(endpoint: string): Promise<APIResponse<T>> {
    return this.makeRequest<T>('GET', endpoint);
  }

  async post<T>(endpoint: string, data?: any): Promise<APIResponse<T>> {
    return this.makeRequest<T>('POST', endpoint, data);
  }

  async put<T>(endpoint: string, data?: any): Promise<APIResponse<T>> {
    return this.makeRequest<T>('PUT', endpoint, data);
  }

  async delete<T>(endpoint: string): Promise<APIResponse<T>> {
    return this.makeRequest<T>('DELETE', endpoint);
  }

  async patch<T>(endpoint: string, data?: any): Promise<APIResponse<T>> {
    return this.makeRequest<T>('PATCH', endpoint, data);
  }

  // Utility methods for token management
  clearTokenCache(): void {
    this.tokenCache = null;
  }

  setRetryConfig(config: Partial<RetryConfig>): void {
    this.retryConfig = { ...this.retryConfig, ...config };
  }
}

export const apiClient = new APIClient();
export default apiClient;